'use client';

import { createContext, useContext, useState } from 'react';
import { questionTranslations } from './legalForm.config';
import { useGivenAnswers } from './legalFormHooks';

type LegalFormAnalyticsContextValue = {
	hasStartedDecisionTool: boolean;
	hasViewedResult: boolean;
	onDecisionToolStarted: () => void;
	onResultViewed: () => void;
};

const LegalFormAnalyticsContext = createContext<
	LegalFormAnalyticsContextValue | undefined
>(undefined);

const wasAnyAnswerGiven = (givenAnswers: Map<string, string | null>) => {
	return Array.from(givenAnswers.values()).some((answer) => answer !== null);
};

export const convertGivenAnswersToSearchParams = (
	givenAnswers: Map<string, string | null>,
) => {
	const searchParams = new URLSearchParams();

	for (const question of questionTranslations) {
		const answer = givenAnswers.get(question.id);

		if (answer != null) {
			searchParams.set(question.id, answer);
		}
	}

	return searchParams;
};

export const LegalFormAnalyticsProvider = ({
	children,
}: {
	children: React.ReactNode;
}) => {
	const givenAnswers = useGivenAnswers();
	const [hasStartedDecisionTool, setHasStartedDecisionTool] = useState(() => {
		return wasAnyAnswerGiven(givenAnswers);
	});
	const [hasViewedResult, setHasViewedResult] = useState(() => {
		return wasAnyAnswerGiven(givenAnswers);
	});

	return (
		<LegalFormAnalyticsContext.Provider
			value={{
				hasStartedDecisionTool,
				hasViewedResult,
				onDecisionToolStarted: () => {
					setHasStartedDecisionTool(true);
				},
				onResultViewed: () => {
					setHasViewedResult(true);
				},
			}}
		>
			{children}
		</LegalFormAnalyticsContext.Provider>
	);
};

export const useLegalFormAnalytics = () => {
	const context = useContext(LegalFormAnalyticsContext);

	if (context == null) {
		throw new Error(
			'useLegalFormAnalytics must be used within a LegalFormAnalyticsProvider',
		);
	}

	return context;
};
